// BeneAIDrawer — BeneAI-01.
//
// Right-side overlay drawer hosting the chat surface. Owns:
//   * lifecycle (open/close, ESC handler, focus restore)
//   * route-context capture per send
//   * orchestration of useBeneAIConversation + drawer state
//
// Persistence: the drawer keeps the same ``conversationId`` and
// transcript in ``BeneAIDrawerProvider`` across close/reopen, so the
// user resumes exactly where they left off. The "fresh chat" path
// is handled by the backend (``conversation_id="new"``); the FE only
// resets when the user explicitly clicks "New chat".

import { Plus, X } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";

import { BeneAIComposer } from "./BeneAIComposer";
import { BeneAIConversationDropdown } from "./BeneAIConversationDropdown";
import { useBeneAIDrawer } from "./BeneAIDrawerContext";
import { BeneAIMessageList } from "./BeneAIMessageList";
import { useBeneAIConversation } from "./useBeneAIConversation";
import { useBeneAIContext } from "./useBeneAIContext";
import type { BeneAIErrorCode } from "./types";


const ERROR_COPY: Record<BeneAIErrorCode, string> = {
  beneai_not_configured: (
    "BeneAI hasn't been set up for this brokerage yet. Ask an admin"
    + " to add a provider key in Settings."
  ),
  no_provider_key: (
    "BeneAI needs a provider key before it can answer. Add one in"
    + " Settings → Integrations."
  ),
  no_managed_key: (
    "BeneAI is in managed-keys mode but the platform's "
    + "Anthropic key isn't configured. Turn off managed mode in "
    + "Settings or contact support."
  ),
  runtime_error: "Something broke on the BeneAI runtime. Try again.",
  provider_error: (
    "The model provider returned an error. Try again — if it keeps"
    + " happening, check the provider key in Settings."
  ),
  message_required: "Type a message before sending.",
  conversation_not_found: "Conversation lost. Starting a new chat.",
  invalid_conversation_id: "Conversation lost. Starting a new chat.",
  session_not_found: "Conversation lost. Starting a new chat.",
  assistant_message_missing:
    "BeneAI didn't return a response. Try again.",
  authentication_required: "Please sign in again.",
  entity_required: "Pick a brokerage from the top-right menu first.",
  confirmation_not_found: "That confirmation is no longer available.",
  confirmation_expired:
    "Confirmation expired before you could resolve it — ask BeneAI again.",
  confirmation_already_resolved:
    "That confirmation has already been confirmed.",
  confirmation_already_cancelled:
    "That confirmation has already been cancelled.",
  permission_denied:
    "Your role doesn't permit that action. Ask an admin if you "
    + "think this is a mistake.",
  tool_failed: "BeneAI couldn't complete that action. Try again.",
  invalid_input:
    "BeneAI couldn't validate the proposed change. Ask it to try again.",
  unknown_tool:
    "BeneAI tried to use a tool that isn't available anymore.",
  unknown: "Something went wrong. Try again.",
};


export function BeneAIDrawer() {
  const drawer = useBeneAIDrawer();
  const context = useBeneAIContext();
  const { isSending, lastError, send } = useBeneAIConversation();
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  // Restore focus to whatever was active before the drawer opened so
  // closing the drawer feels like a true overlay.
  useEffect(() => {
    if (drawer.open) {
      lastFocusedRef.current =
        (document.activeElement as HTMLElement | null) ?? null;
    } else if (lastFocusedRef.current !== null) {
      const target = lastFocusedRef.current;
      lastFocusedRef.current = null;
      // Defer focus restoration so it lands after the drawer
      // transition unmounts the overlay.
      window.requestAnimationFrame(() => target.focus?.());
    }
  }, [drawer.open]);

  // ESC closes — but only when the drawer is actually mounted.
  useEffect(() => {
    if (!drawer.open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        drawer.closeDrawer();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [drawer.open, drawer.closeDrawer]);

  const handleSend = useCallback(
    async (body: string) => {
      const result = await send(body, context);
      if (!result.ok && result.error?.code === "conversation_not_found") {
        // The session vanished mid-flight (rare). Reset and let the
        // user retry against a fresh conversation.
        drawer.reset();
      }
    },
    [send, context, drawer],
  );

  const handleNewChat = useCallback(() => {
    drawer.reset();
  }, [drawer]);

  if (!drawer.open) return null;

  const helperCode = lastError?.code;
  const helperText = helperCode ? ERROR_COPY[helperCode] : undefined;

  return (
    <div
      data-testid="beneai-drawer-overlay"
      // Click-outside closes; the inner panel stops propagation so a
      // click on the drawer body doesn't dismiss.
      onClick={drawer.closeDrawer}
      className="fixed inset-0 z-50 flex justify-end bg-canvas/40 backdrop-blur-sm"
    >
      <aside
        data-testid="beneai-drawer"
        role="dialog"
        aria-label="BeneAI"
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-md flex-col border-l border-border-subtle bg-canvas shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-text-primary">
              BeneAI
            </span>
            <BeneAIConversationDropdown />
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="beneai-drawer-new"
              aria-label="New BeneAI chat"
              onClick={handleNewChat}
              className="inline-flex h-7 w-7 items-center justify-center rounded text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
              title="New chat"
            >
              <Plus size={14} aria-hidden="true" />
            </button>
            <button
              type="button"
              data-testid="beneai-drawer-close"
              aria-label="Close BeneAI"
              onClick={drawer.closeDrawer}
              className="inline-flex h-7 w-7 items-center justify-center rounded text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        </header>

        <BeneAIMessageList
          messages={drawer.messages}
          isAwaitingAssistant={isSending}
        />

        <BeneAIComposer
          isSending={isSending}
          onSend={handleSend}
          helperText={helperText}
          helperTone={helperCode ? "error" : "info"}
          autoFocus
        />
      </aside>
    </div>
  );
}
